import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, switchMap, throwError, timer } from 'rxjs';
import { ActiveToast, ToastrService } from 'ngx-toastr';

import { DEPLOYMENT_TOAST_TEXT, isDeploymentInProgress, isServerError, silenceServerErrorTurn } from '@services/helpers/server-error-silence';

const RETRY_DELAY_MS = 5000;

/**
 * Central 5xx handling — see docs/DEPLOYMENT_IN_PROGRESS_TOASTER_FRONTEND.md:
 * - `DEPLOYMENT_IN_PROGRESS`: show "Deployment on the way" and retry once. Retrying is safe for
 *   every method: a request that got this response never reached the app.
 * - Any 5xx that still fails: logged, marked `silent`, and delivered inside a silent turn so no
 *   screen shows it. Callers still get their error callback, so spinners stop and forms stay open.
 *
 * Registered last so it sits closest to the backend: the retry reuses the request exactly as the
 * earlier interceptors already prepared it (auth header included), and the silent turn is open
 * before any of them see the error.
 */
@Injectable()
export class DeploymentInProgressInterceptor implements HttpInterceptor {
  /** A page fires many requests at once during a deploy; they should share one toast. */
  private deployToast: ActiveToast<any> | null = null;

  constructor(private toastr: ToastrService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.send(request, next, true);
  }

  private send(request: HttpRequest<any>, next: HttpHandler, canRetry: boolean): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError(error => {
        if (!(error instanceof HttpErrorResponse) || !isServerError(error)) {
          return throwError(() => error);
        }
        if (isDeploymentInProgress(error)) {
          this.notifyDeployment();
          if (canRetry) {
            return timer(RETRY_DELAY_MS).pipe(switchMap(() => this.send(request, next, false)));
          }
        }
        if (error.error && typeof error.error === 'object') {
          error.error.silent = true;
        }
        console.error('[API 5xx]', request.method, request.urlWithParams, error.status);
        silenceServerErrorTurn();
        return throwError(() => error);
      })
    );
  }

  private notifyDeployment(): void {
    if (this.deployToast) {
      return;
    }
    this.deployToast = this.toastr.info(DEPLOYMENT_TOAST_TEXT);
    this.deployToast?.onHidden.subscribe(() => (this.deployToast = null));
  }
}
